<template>
  <div class="sb-numberfield" v-bind="$attrs">
    <label v-if="label" :for="uid" class="sb-numberfield__label">
      {{ label }}

      <span v-if="required" class="sb-numberfield__required" aria-hidden="true">
        *
      </span>
    </label>
    <div
      class="sb-numberfield__inner"
      :class="{
        'sb-numberfield__inner--error': error,
      }"
    >
      <input
        :id="uid"
        :key="componentKey"
        :value="computedValue"
        class="sb-numberfield__input"
        :data-testid="dataTestid"
        :autocomplete="autocomplete"
        :placeholder="placeholder"
        :aria-describedby="ariaDescribedby"
        :aria-invalid="error"
        :aria-required="required"
        :name="name"
        type="number"
        :min="min"
        :max="max"
        :step="step"
        :readonly="readonly || disabled"
        :class="{
          'sb-numberfield__input--error': error,
        }"
        :required="required"
        :aria-disabled="disabled"
        @input="handleInputEvent"
        @focus="handleFocusEvent"
        @blur="handleBlurEvent"
        @change="handleChangeEvent"
        @keydown="handleKeydownEvent"
      />
      <SbIcon v-if="error" name="octagon-alert" color="danger" />
      <SbIconButton
        tabindex="-1"
        :is-disabled="isMinArrowDisabled"
        :label="decreaseButtonLabel"
        icon="minus"
        size="small"
        variant="tertiary"
        @click="decreaseValue"
      />
      <hr class="sb-numberfield__separator" />
      <SbIconButton
        tabindex="-1"
        :is-disabled="isMaxArrowDisabled"
        :label="increaseButtonLabel"
        icon="plus"
        size="small"
        variant="tertiary"
        @click="increaseValue"
      />
      <slot />
    </div>

    <span
      v-if="showError"
      :id="`${uid}-error`"
      class="sb-numberfield__message sb-numberfield__message--error"
    >
      {{ errorMessage }}
    </span>

    <span
      v-if="description && !showError"
      :id="`${uid}-description`"
      class="sb-numberfield__message"
    >
      {{ description }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, useId, type HTMLAttributes } from 'vue'
import SbIcon from '../Icon'
import SbIconButton from '../IconButton'
import {
  isEqualOrGreaterThan,
  isEqualOrLessThan,
  isGreaterThan,
  isLessThan,
  isValidNumber,
} from '../../utils'
import { getPrecision } from './helpers'
import { numberFromString } from '../../utils/parsers'

export type NumberFieldProps = {
  /**
   * Numberfield value.
   */
  modelValue?: number | null

  /**
   * Numberfield label.
   */
  label: string

  /**
   * Numberfield placeholder.
   */
  placeholder?: string

  /**
   * Numberfield error state. Indicates that the numberfield has an error state. It shows an error icon.
   */
  error?: boolean

  /**
   * Numberfield error message. The message that will be shown when the numberfield has an error state.
   */
  errorMessage?: string

  /**
   * Numberfield description. A description that will be shown below the Numberfield. When the error state is true, the description will be hidden.
   */
  description?: string

  /**
   * Numberfield disabled state. The disable state does not remove the focus of the input.
   */
  disabled?: boolean

  /**
   * Disables interaction with the field.
   */
  readonly?: boolean

  /**
   * Numberfield required state.
   */
  required?: boolean

  /**
   * Numberfield id attribute.
   */
  id?: string

  /**
   * Numberfield name attribute. This property is used as the parameter of the name attribute of the input element.
   */
  name?: string

  /**
   * Numberfield autocomplete attribute. This property is used as the parameter of the autocomplete attribute of the input element.
   */
  autocomplete?: string

  /**
   * Numberfield step attribute. This property is used as the parameter of the step attribute of the input element.
   */
  step?: number

  /**
   * Numberfield data-testid attribute.
   */
  dataTestid?: string

  /**
   * Numberfield min value attribute.
   */
  min?: number

  /**
   * Numberfield max value attribute.
   */
  max?: number

  /**
   * Define how many decimal places the numberfield should have.
   * This is used to round the value of the numberfield.
   * If the value is not a number, it will be rounded to the nearest integer.
   */
  precision?: number

  /**
   * Decrease button label.
   */
  decreaseButtonLabel?: string

  /**
   * Increase button label.
   */
  increaseButtonLabel?: string
  // eslint-disable-next-line vue/prop-name-casing
} & /* @vue-ignore */ HTMLAttributes

const props = withDefaults(defineProps<NumberFieldProps>(), {
  dataTestid: 'sb-numberfield__input',
  step: 1,
  decreaseButtonLabel: 'Decrease value',
  increaseButtonLabel: 'Increase value',
})

type Emits = {
  /**
   * Emitted when the value of the input field changes.
   * This event is emitted after the user leaves the input field.
   * It emits two values: the new value and the previous value.
   */
  (
    e: 'change',
    newValue: number | undefined,
    previousValue: number | undefined,
  ): void

  /**
   * Emitted when the value of the input field changes.
   * This event is emitted after the user leaves the input field.
   * It only emits the new value.
   */
  (e: 'update:modelValue', value: number | undefined): void

  /**
   * Forward the blur event to the parent component.
   */
  (e: 'blur', value: FocusEvent): void

  /**
   * Forward the focus event to the parent component.
   */
  (e: 'focus', value: FocusEvent): void

  /**
   * Forward the keydown event to the parent component.
   */
  (e: 'keydown', value: KeyboardEvent): void
}

const emit = defineEmits<Emits>()

const internalValue = ref<number | undefined>()
const userInput = ref<string | undefined>()
const componentKey = ref(0)

const uid = computed(() => props.id ?? `numberfield-${useId()}`)

const isMinArrowDisabled = computed(() => {
  return (
    props.disabled ||
    isLessThan(
      calculateDecreasement(internalValue.value, props.step),
      props.min,
    )
  )
})

const isMaxArrowDisabled = computed(() => {
  return (
    props.disabled ||
    isGreaterThan(
      calculateIncreasement(internalValue.value, props.step),
      props.max,
    )
  )
})

const precisionNumber = computed(() => {
  const stepPrecision = getPrecision(props.step)

  if (props.precision && stepPrecision > props.precision) {
    console.warn(
      '[NumberField] - precision should not be less than the decimal places of step',
    )
    return props.precision
  } else {
    return Math.max(getPrecision(props.modelValue), stepPrecision)
  }
})

const computedValue = computed(() => {
  if (userInput.value !== undefined) {
    return userInput.value
  }

  if (typeof internalValue.value === 'number' && props.precision) {
    return internalValue.value.toFixed(props.precision)
  }

  return internalValue.value
})

const showError = computed(() => props.error && props.errorMessage)

const ariaDescribedby = computed(() => {
  const describedBy = []
  if (props.error && props.errorMessage) {
    describedBy.push(`${uid.value}-error`)
  } else if (props.description) {
    describedBy.push(`${uid.value}-description`)
  }
  return describedBy.join(' ')
})

watch(
  () => props.modelValue,
  (value) => {
    let newValue = value === undefined ? value : Number(value)

    if (newValue && isNaN(newValue)) {
      return
    }

    if (newValue && props.precision) {
      newValue = getClosestPreciseNumber(newValue, props.precision)
    }

    if (isEqualOrGreaterThan(newValue, props.max) && props.max !== undefined) {
      newValue = props.max
    }

    if (isEqualOrLessThan(newValue, props.min) && props.min !== undefined) {
      newValue = props.min
    }

    internalValue.value = newValue
    userInput.value = undefined
    emit('update:modelValue', newValue)
  },
  { immediate: true },
)

watch(
  () => [props.min, props.max],
  ([min, max]) => {
    let value = props.modelValue

    if (!isValidNumber(value)) {
      return
    }

    if (isValidNumber(max) && isEqualOrGreaterThan(value, max)) {
      value = max
    }

    if (isValidNumber(min) && isEqualOrLessThan(value, min)) {
      value = min
    }

    setInternalValue(value)
  },
)

function getClosestPreciseNumber(num: number, precision?: number) {
  if (precision === undefined) {
    precision = precisionNumber.value
  }

  return parseFloat(
    (
      Math.round(num * Math.pow(10, precision)) / Math.pow(10, precision)
    ).toString(),
  )
}

function calculateIncreasement(value?: number, step?: number) {
  if (value === undefined || typeof value !== 'number' || step === undefined) {
    return 0
  }

  const precisionFactor = Math.pow(10, precisionNumber.value)

  return getClosestPreciseNumber(
    (precisionFactor * value + precisionFactor * step) / precisionFactor,
  )
}

function calculateDecreasement(value?: number, step?: number) {
  if (value === undefined || typeof value !== 'number' || step === undefined) {
    return 0
  }

  const precisionFactor = Math.pow(10, precisionNumber.value)

  return getClosestPreciseNumber(
    (precisionFactor * value - precisionFactor * step) / precisionFactor,
  )
}

function increaseValue() {
  if (isMaxArrowDisabled.value || props.readonly) {
    return
  }

  const value = props.modelValue ?? 0
  const newValue = calculateIncreasement(value, props.step)
  setInternalValue(newValue)
}

function decreaseValue() {
  if (isMinArrowDisabled.value || props.readonly) {
    return
  }

  const value = props.modelValue ?? 0
  const newValue = calculateDecreasement(value, props.step)
  setInternalValue(newValue)
}

function setInternalValue(newValue?: number) {
  if (typeof newValue === 'number' && props.precision !== undefined) {
    newValue = getClosestPreciseNumber(newValue, props.precision)
  }

  if (isEqualOrGreaterThan(newValue, props.max)) {
    newValue = props.max
  }

  if (isEqualOrLessThan(newValue, props.min)) {
    newValue = props.min
  }

  if (internalValue.value === newValue) {
    return
  }

  userInput.value = undefined
  emit('update:modelValue', newValue)
  emit('change', newValue, internalValue.value)
  internalValue.value = newValue
}

function handleBlurEvent(e: FocusEvent) {
  const target = e.target as HTMLInputElement
  if (target.value === '') {
    componentKey.value++
  }
  emit('blur', e)
}

function handleFocusEvent(e: FocusEvent) {
  emit('focus', e)
}

function handleInputEvent(e: Event) {
  const target = e.target as HTMLInputElement
  userInput.value = target.value
}

function handleChangeEvent(e: Event) {
  const target = e.target as HTMLInputElement
  const newValue = numberFromString(target.value)
  userInput.value = undefined
  setInternalValue(newValue)
}

function handleKeydownEvent(e: KeyboardEvent) {
  if (e.keyCode === 38) {
    e.preventDefault()
    increaseValue()
  } else if (e.keyCode === 40) {
    e.preventDefault()
    decreaseValue()
  }
  emit('keydown', e)
}
</script>
